import { ReactNode, useState } from 'react';
import classes from 'classnames';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { WELCOME_BACK_NEW } from 'Router/routes';
import Logo from 'assets/logo.svg?react';
import LogoFooter from 'assets/logo-muted.svg?react';
import LogoSmall from 'assets/logo-small.svg?react';
import HelpIcon from 'assets/icons/calculator-help.svg?react';
import useCalculator from 'hooks/calculator/useCalculator';
import Progress from '../Progress/Progress';
import ProgressBar from '../ProgressBar/ProgressBar';

import styles from './Layout.module.scss';
import SidebarAmountAndMTP from './SidebarAmountAndMTP';
import Drawer from './Drawer';
import MobileCalculalorChevron from './MobileCalculatorChevron';
import DesktopCalculatorButton from './DesktopCalculatorButton';

const ROUTES_NO_MOBILE_PADDING = [WELCOME_BACK_NEW];

export type Props = {
  showSidebar?: boolean;
  steps: Array<string>;
  currentStep: number;
  children: ReactNode;
  showFooter?: boolean;
  showProgress?: boolean;
  dataTest?: string;
  transitionDirection?: 'top' | 'bottom';
  url?: string;
  renderNavigationItems?: () => ReactNode;
  withCalculator?: boolean;
  progressDisabled?: boolean;
  nextStepsDisabled?: boolean;
  layoutStyles?: string;
};

function Layout({
  steps,
  currentStep,
  children,
  showSidebar = true,
  showFooter = false,
  showProgress = true,
  dataTest,
  transitionDirection,
  url,
  withCalculator = false,
  renderNavigationItems,
  progressDisabled = false,
  nextStepsDisabled = false,
  layoutStyles,
}: Props) {
  const { t } = useTranslation('common');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { offer, backupCurrentAmount, recoverPreviousAmount, isClientError } =
    useCalculator();
  const { pathname } = useLocation();

  const handleDrawerOpen = () => {
    backupCurrentAmount();
    setDrawerOpen(true);
  };

  const handleCancel = () => {
    recoverPreviousAmount();
    setDrawerOpen(false);
  };

  const handleConfirm = () => {
    setDrawerOpen(false);
  };

  const toggleDrawerOpen = () => {
    if (!drawerOpen) {
      handleDrawerOpen();
    } else {
      handleCancel();
    }
  };

  const mobilePadding = ROUTES_NO_MOBILE_PADDING.includes(pathname)
    ? ''
    : 'py-12 px-5';

  const showProgressWithoutSidebar = !showSidebar && showProgress;

  return (
    <>
      {/* Wrapper */}
      <div
        data-test={dataTest}
        className={twMerge('w-full min-h-dvh flex flex-col', 'lg:flex-row')}
      >
        {/*

        SIDEBAR START

        */}
        {/* Mobile topbar or Desktop sidebar */}
        {(showSidebar || showProgress) && (
          <aside
            className={twMerge(
              `flex ${
                showProgressWithoutSidebar ? 'lg:hidden' : ''
              } flex-col gap-2 z-20 bg-background w-full shrink-0`,
              'lg:pt-11 lg:px-5 lg:pb-6 lg:relative lg:w-48 lg:border-r lg:gap-6',
            )}
          >
            {/* Desktop sidebar title */}
            <div className="hidden lg:block  border-b  pb-4 ">
              <div className="w-20 leading-5 font-medium text-lg">
                {t('loan_overview')}
              </div>
            </div>

            {/* Mobile logo, progress bar and help icon */}
            {showProgress && (
              <div className="lg:hidden flex items-center px-5 py-5 gap-5 border-b">
                <LogoSmall
                  className="shrink-0"
                  data-test="layout-sidebar-logo-mobile"
                />
                <ProgressBar steps={steps.length} currentStep={currentStep} />
                <HelpIcon className="shrink-0" data-test="layout-help-icon" />
              </div>
            )}

            <div
              className={`${
                showProgressWithoutSidebar ? 'hidden' : 'flex'
              } items-center justify-between px-5 py-2 border-b lg:px-0 lg:w-full lg:border-b-0`}
            >
              {/* Desktop and mobile amount and term */}
              <SidebarAmountAndMTP
                amount={offer?.newPrincipal}
                mtp={offer?.loanExtensionFees[0]?.fee}
                isError={isClientError}
                drawerOpen={drawerOpen}
              />

              {/* Mobile chevron for opening calculator */}
              {withCalculator && (
                <MobileCalculalorChevron
                  onClick={toggleDrawerOpen}
                  active={drawerOpen}
                />
              )}
            </div>

            {/* Desktop button for opening calculator */}
            {withCalculator && (
              <DesktopCalculatorButton
                onClick={handleDrawerOpen}
                disabled={drawerOpen}
              />
            )}

            {/* Desktop example loan text */}
            <div
              className="hidden lg:block lg:text-xs lg:text-muted"
              data-test="drawer-calculator-example"
              dangerouslySetInnerHTML={{
                __html: import.meta.env.VITE_CALCULATOR_HTML_TEXT,
              }}
            />

            {/* Desktop footer inside sidebar */}
            {showFooter && (
              <footer className="hidden lg:block lg:mt-auto">
                <LogoFooter />
                <div className="text-muted text-xs">{t('footer_text')}</div>
              </footer>
            )}
          </aside>
        )}
        {/*

        SIDEBAR END

        */}

        <div
          className={twMerge(
            'w-full flex flex-col relative h-full z-10',
            'lg:w-80 lg:border-r lg:px-11 lg:py-12',
          )}
        >
          {/* Drawer with calculator and offer details */}
          <Drawer
            drawerOpen={drawerOpen}
            handleCancel={handleCancel}
            handleConfirm={handleConfirm}
          />

          {/* Desktop progress */}
          <div className="hidden lg:flex lg:w-80 lg:h-full shrink-0">
            <div>
              <Logo className="mb-48" data-test="layout-sidebar-logo" />
              {showProgress && (
                <Progress
                  steps={steps}
                  currentStep={currentStep}
                  disabled={progressDisabled}
                  nextStepsDisabled={nextStepsDisabled}
                />
              )}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div
          className={classes(
            twMerge(
              `w-full max-w-3xl ${mobilePadding} lg:py-12 lg:px-5 mx-auto flex-grow`,
              'lg:py-40 lg:px-16 lg:ml-0',
              layoutStyles,
            ),
            drawerOpen ? styles.blur : styles['no-blur'],
          )}
        >
          {/* Desktop logout button */}
          <nav className="hidden lg:flex lg:justify-end lg:absolute lg:top-12 lg:right-20">
            {renderNavigationItems && renderNavigationItems()}
          </nav>

          <main>
            <div
              key={url}
              className={classes(
                'min-h-full',
                transitionDirection === 'top'
                  ? styles['top-fade-in']
                  : styles['bottom-fade-in'],
              )}
            >
              {children}
            </div>
          </main>
        </div>

        {/* Mobile footer */}
        {showFooter && (
          <footer
            className={classes(
              twMerge('bg-white border-t px-5 py-10', 'lg:hidden'),
              drawerOpen ? styles.blur : styles['no-blur'],
            )}
          >
            <div className="flex justify-between mb-3 items-center">
              <LogoFooter />
              {renderNavigationItems && renderNavigationItems()}
            </div>
            <div className="text-muted text-xs">{t('footer_text')}</div>
          </footer>
        )}
      </div>
    </>
  );
}

export default Layout;
